/**
 * Whiteboard spec contract.
 *
 * A "whiteboard" is a tutor-summoned interactive surface shown during a session.
 * The voice agent (main3.py) publishes specs over the LiveKit data channel on
 * `WHITEBOARD_TOPIC`, and learner interactions are published back on the same
 * topic so the tutor can react by voice.
 *
 * The text-practice path generates the same specs via `/api/whiteboard/generate`.
 *
 * This file is the single source of truth for the TypeScript side; the Python
 * mirror lives in `lib/whiteboard_runtime.py`. Keep the wire shapes in sync.
 */
import { hasExactlyOneFillInGapBlank } from "@/lib/fill-in-gap-exercise";
import { z } from "zod";
import { ReferenceSchema } from "./generated/contract";
import { StudyExerciseSchema } from "./studySpec";

export const WHITEBOARD_TOPIC = "whiteboard";

// ─── Exercise specs ──────────────────────────────────────────────────

/** Fill the gap in a sentence — ideally the learner's own sentence. */
export const GapFillSpecSchema = z.object({
  kind: z.literal("gap_fill"),
  id: z.string(),
  /** Sentence with `___` (three underscores) marking the blank. */
  sentence: z.string(),
  /** 2–4 tappable options; must include the answer. */
  options: z.array(z.string()).min(2).max(4),
  answer: z.string(),
  translation: z.string().optional(),
  hint: z.string().optional(),
  explanation: z.string().optional(),
});

/** A 5-second tap question asked mid-conversation. */
const QuickChoiceSpecBaseSchema = z.object({
  kind: z.literal("quick_choice"),
  id: z.string(),
  question: z.string(),
  options: z.array(z.string()).min(2).max(4),
  /** Omitted => a steering choice with no right answer (e.g. "what next?"). */
  answerIndex: z.number().int().min(0).optional(),
  explanation: z.string().optional(),
});

export const QuickChoiceSpecSchema = QuickChoiceSpecBaseSchema.superRefine((spec, ctx) => {
  if (spec.answerIndex !== undefined && spec.answerIndex >= spec.options.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["answerIndex"],
      message: "answerIndex must point to an existing option.",
    });
  }
});

/** Rebuild a sentence from scrambled word tiles. */
export const WordTilesSpecSchema = z.object({
  study: StudyExerciseSchema.optional(),
  kind: z.literal("word_tiles"),
  id: z.string(),
  /** Pre-shuffled tiles. */
  tiles: z.array(z.string()).min(2).max(12),
  /** The target sentence the tiles assemble into. */
  answer: z.string(),
  translation: z.string().optional(),
  hint: z.string().optional(),
});

/** A non-interactive story that stays visible while the tutor discusses it. */
export const ReadingStorySpecSchema = z.object({
  study: StudyExerciseSchema.optional(),
  kind: z.literal("reading_story"),
  id: z.string(),
  /** Optional story title. Discussion questions never belong in this field. */
  title: z.string().trim().min(1).max(120).optional(),
  /** Original, level-appropriate target-language story text only. */
  story: z.string().trim().min(40).max(4000),
});

/** Trusted, parameter-driven maths renderer. It never accepts SVG or freehand data. */
export const EquationBalanceSpecSchema = z
  .object({
    kind: z.literal("equation_balance"),
    id: z.string().min(1),
    taskId: z.string().min(1),
    attemptId: z.string().min(1),
    graphTopicId: z.string().min(1),
    knowledgePointId: z.string().min(1),
    generatorId: z.string().min(1).nullable().optional(),
    title: z.string().min(1),
    prompt: z.string().min(1),
    equation: z.string().min(3),
    left: z.string().min(1),
    right: z.string().min(1),
    semanticRegionIds: z.array(z.string().min(1)).min(3),
    choices: z
      .array(
        z
          .object({
            id: z.string().min(1),
            label: z.string().min(1),
            operationId: z.string().min(1),
            correct: z.boolean(),
          })
          .strict()
      )
      .min(2)
      .max(4),
    answerChoices: z
      .array(
        z
          .object({
            id: z.string().min(1),
            label: z.string().min(1),
            answerId: z.string().min(1),
            correct: z.boolean(),
          })
          .strict()
      )
      .min(2)
      .max(4),
    hint: z.string().nullable().optional(),
    guidedStep: z.string().nullable().optional(),
    lastStep: z
      .object({
        stepId: z.string().min(1),
        operationId: z.string().min(1),
        operationLabel: z.string().min(1),
        workingEquation: z.string().min(3).max(240).optional(),
        visualDetail: z.enum(["result_only", "show_both_sides"]).optional(),
        fromEquation: z.string().min(3),
        toEquation: z.string().min(3),
      })
      .strict()
      .nullable()
      .optional(),
    status: z.enum(["ready", "retry", "guided", "verifying", "resolved"]).default("ready"),
  })
  .strict();

// ─── Composed exercises (Level 1 generative UX) ──────────────────────
//
// Instead of picking a fixed exercise shape, the tutor composes a novel
// exercise from a small vocabulary of interaction primitives ("blocks").
// Every block emits standardized events, so tutor awareness is preserved
// no matter what the model invents.

export const TextBlockSchema = z.object({
  type: z.literal("text"),
  content: z.string(),
  style: z.enum(["normal", "muted", "emphasis"]).optional(),
});

/** Tap one (or several) options. `correct` omitted => steering choice. */
export const TapGroupBlockSchema = z.object({
  type: z.literal("tap_group"),
  label: z.string().optional(),
  options: z.array(z.string()).min(2).max(8),
  /** The correct option(s). With `multiple`, learner must match the full set. */
  correct: z.array(z.string()).optional(),
  multiple: z.boolean().optional(),
});

/** Sentence with one or more ___ gaps filled from a shared word pool. */
export const SlotSentenceBlockSchema = z.object({
  type: z.literal("slot_sentence"),
  label: z.string().optional(),
  /** Sentence with each gap marked as ___ (three underscores). */
  sentence: z.string(),
  /** Tappable pool; should contain the answers plus distractors. */
  pool: z.array(z.string()).min(1).max(8),
  /** Correct fillers in gap order. */
  answers: z.array(z.string()).min(1).max(6),
});

/** Sort items into labeled buckets (e.g. ser / estar). */
export const BucketSortBlockSchema = z.object({
  type: z.literal("bucket_sort"),
  label: z.string().optional(),
  buckets: z.array(z.string()).min(2).max(4),
  items: z
    .array(z.object({ text: z.string(), bucket: z.string() }))
    .min(2)
    .max(10),
});

/** Tap the target words inside a passage (e.g. "find every error"). */
export const HighlightTargetBlockSchema = z.object({
  type: z.literal("highlight_target"),
  label: z.string().optional(),
  text: z.string(),
  /** Words/tokens in `text` the learner must tap. */
  targets: z.array(z.string()).min(1).max(8),
});

/** Arrange items into the correct order. `items` is the correct order. */
export const OrderingBlockSchema = z.object({
  type: z.literal("ordering"),
  label: z.string().optional(),
  items: z.array(z.string()).min(2).max(8),
});

/** A continuous dimension (formality, certainty, ...). No right answer. */
export const SliderBlockSchema = z.object({
  type: z.literal("slider"),
  label: z.string(),
  leftLabel: z.string(),
  rightLabel: z.string(),
  /** Optional discrete stops; learner snaps to one. */
  steps: z.array(z.string()).min(2).max(7).optional(),
});

/** Peek-able content. Peeks are reported to the tutor. */
export const RevealBlockSchema = z.object({
  type: z.literal("reveal"),
  label: z.string(),
  content: z.string(),
});

/** Repair the learner's own sentence by choosing replacements for marked spans. */
export const SentenceRepairBlockSchema = z.object({
  type: z.literal("sentence_repair"),
  label: z.string().optional(),
  original: z.string(),
  targets: z
    .array(
      z.object({
        text: z.string(),
        options: z.array(z.string()).min(2).max(4),
        answer: z.string(),
        note: z.string().optional(),
      })
    )
    .min(1)
    .max(4),
  corrected: z.string().optional(),
});

/** Match meaning chunks, phrases, functions, or forms across two columns. */
export const PairMatchBlockSchema = z.object({
  type: z.literal("pair_match"),
  label: z.string().optional(),
  leftLabel: z.string().optional(),
  rightLabel: z.string().optional(),
  leftItems: z
    .array(z.object({ id: z.string(), text: z.string() }))
    .min(2)
    .max(8),
  rightItems: z
    .array(z.object({ id: z.string(), text: z.string(), leftId: z.string() }))
    .min(2)
    .max(8),
});

/** Focus a phrase on a sound, stress, tone, liaison, or syllable target. */
export const PronunciationFocusBlockSchema = z.object({
  type: z.literal("pronunciation_focus"),
  label: z.string().optional(),
  phrase: z.string(),
  focus: z.string(),
  targets: z
    .array(
      z.object({
        text: z.string(),
        note: z.string().optional(),
      })
    )
    .min(1)
    .max(6),
});

/** Learner taps what they heard from close words or phrases. */
export const ListeningDiscriminationBlockSchema = z.object({
  type: z.literal("listening_discrimination"),
  label: z.string().optional(),
  prompt: z.string().optional(),
  options: z.array(z.string()).min(2).max(6),
  answer: z.string(),
  note: z.string().optional(),
});

/** A roleplay phrase bank where the learner chooses a phrase to use next. */
export const PhraseBankRoleplayBlockSchema = z.object({
  type: z.literal("phrase_bank_roleplay"),
  label: z.string().optional(),
  scenario: z.string(),
  tutorLine: z.string().optional(),
  goal: z.string(),
  phrases: z
    .array(
      z.object({
        text: z.string(),
        function: z.string().optional(),
        register: z.string().optional(),
        translation: z.string().optional(),
        recommended: z.boolean().optional(),
      })
    )
    .min(2)
    .max(6),
});

/** Controlled transformations: tense, person, negation, question form, register. */
export const TransformationFrameBlockSchema = z.object({
  type: z.literal("transformation_frame"),
  label: z.string().optional(),
  instruction: z.string(),
  base: z.string(),
  items: z
    .array(
      z.object({
        cue: z.string(),
        options: z.array(z.string()).min(2).max(4),
        answer: z.string(),
        note: z.string().optional(),
      })
    )
    .min(1)
    .max(5),
});

/** Compact paradigm table: conjugations, endings, agreement, cases, or forms. */
export const MorphologyGridBlockSchema = z.object({
  type: z.literal("morphology_grid"),
  label: z.string().optional(),
  pattern: z.string(),
  rows: z
    .array(
      z.object({
        label: z.string(),
        cells: z
          .array(
            z.object({
              cue: z.string(),
              options: z.array(z.string()).min(2).max(4),
              answer: z.string(),
              note: z.string().optional(),
            })
          )
          .min(1)
          .max(4),
      })
    )
    .min(1)
    .max(4),
});

/** Compare close forms directly: natural/unnatural, polite/direct, tense/aspect, etc. */
export const ContrastPairBlockSchema = z.object({
  type: z.literal("contrast_pair"),
  label: z.string().optional(),
  prompt: z.string(),
  pairs: z
    .array(
      z.object({
        left: z.string(),
        right: z.string(),
        answer: z.enum(["left", "right", "both"]),
        focus: z.string().optional(),
        explanation: z.string().optional(),
      })
    )
    .min(1)
    .max(4),
});

/** Place examples on a timeline or aspect line: before/now/after, ongoing/done, etc. */
export const TimelineBuilderBlockSchema = z.object({
  type: z.literal("timeline_builder"),
  label: z.string().optional(),
  prompt: z.string(),
  anchors: z.array(z.string()).min(2).max(5),
  items: z
    .array(
      z.object({
        text: z.string(),
        anchor: z.string(),
        note: z.string().optional(),
      })
    )
    .min(1)
    .max(6),
});

/** Label phrases by communicative function: request, soften, refuse, clarify, etc. */
export const DiscourseFunctionBlockSchema = z.object({
  type: z.literal("discourse_function"),
  label: z.string().optional(),
  prompt: z.string().optional(),
  functions: z.array(z.string()).min(2).max(6),
  phrases: z
    .array(
      z.object({
        text: z.string(),
        answer: z.string(),
        note: z.string().optional(),
      })
    )
    .min(1)
    .max(6),
});

/** Choose the translation that is natural in context, not just literal. */
export const TranslationChoiceBlockSchema = z.object({
  type: z.literal("translation_choice"),
  label: z.string().optional(),
  source: z.string(),
  prompt: z.string().optional(),
  options: z
    .array(
      z.object({
        text: z.string(),
        quality: z.enum(["natural", "literal", "wrong"]).optional(),
        note: z.string().optional(),
      })
    )
    .min(2)
    .max(4),
  answer: z.string(),
});

export const ComposedBlockSchema = z.discriminatedUnion("type", [
  TextBlockSchema,
  TapGroupBlockSchema,
  SlotSentenceBlockSchema,
  BucketSortBlockSchema,
  HighlightTargetBlockSchema,
  OrderingBlockSchema,
  SliderBlockSchema,
  RevealBlockSchema,
  SentenceRepairBlockSchema,
  PairMatchBlockSchema,
  PronunciationFocusBlockSchema,
  ListeningDiscriminationBlockSchema,
  PhraseBankRoleplayBlockSchema,
  TransformationFrameBlockSchema,
  MorphologyGridBlockSchema,
  ContrastPairBlockSchema,
  TimelineBuilderBlockSchema,
  DiscourseFunctionBlockSchema,
  TranslationChoiceBlockSchema,
]);

export const ComposedSpecSchema = z.object({
  generated: ReferenceSchema.optional(),
  streaming: ReferenceSchema.optional(),
  study: StudyExerciseSchema.optional(),
  kind: z.literal("composed"),
  id: z.string(),
  title: z.string().optional(),
  blocks: z.array(ComposedBlockSchema).min(1).max(6),
});

export const WhiteboardSpecSchema = z
  .discriminatedUnion("kind", [
    GapFillSpecSchema,
    QuickChoiceSpecBaseSchema,
    WordTilesSpecSchema,
    ReadingStorySpecSchema,
    EquationBalanceSpecSchema,
    ComposedSpecSchema,
  ])
  .superRefine((spec, ctx) => {
    if ("study" in spec && spec.study) {
      const expected =
        spec.study.family === "reading"
          ? "reading_story"
          : spec.study.family === "word_tiles"
            ? "word_tiles"
            : "composed";
      if (spec.kind !== expected)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Study kind does not match its content.",
        });
    }
    if (spec.kind === "gap_fill" && !hasExactlyOneFillInGapBlank(spec.sentence)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sentence"],
        message: "gap_fill needs exactly one ___ blank.",
      });
    }
    if (
      spec.kind === "quick_choice" &&
      spec.answerIndex !== undefined &&
      spec.answerIndex >= spec.options.length
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["answerIndex"],
        message: "answerIndex must point to an existing option.",
      });
    }
  });

export type GapFillSpec = z.infer<typeof GapFillSpecSchema>;
export type QuickChoiceSpec = z.infer<typeof QuickChoiceSpecSchema>;
export type WordTilesSpec = z.infer<typeof WordTilesSpecSchema>;
export type ReadingStorySpec = z.infer<typeof ReadingStorySpecSchema>;
export type EquationBalanceSpec = z.infer<typeof EquationBalanceSpecSchema>;
export type ComposedBlock = z.infer<typeof ComposedBlockSchema>;
export type ComposedSpec = z.infer<typeof ComposedSpecSchema>;
export type WhiteboardSpec = z.infer<typeof WhiteboardSpecSchema>;
export type WhiteboardKind = WhiteboardSpec["kind"];

// ─── Live board mutations (Level 3 generative UX scaffolding) ────────
//
// The live session client renders these as tutor markups on the current board:
// highlights, callout marks, free-position teaching drawings, annotations, hint changes,
// option changes, and word-tile locks.

export const WhiteboardMarkStyleSchema = z.enum(["underline", "circle", "box", "arrow"]);
export const WhiteboardDrawingShapeSchema = z.enum([
  "arrow",
  "line",
  "circle",
  "box",
  "bracket",
  "path",
]);
export const WhiteboardSceneElementSchema = z
  .object({
    id: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/),
    type: z.enum(["text", "equation", "statement"]),
    content: z
      .string()
      .trim()
      .min(1)
      .max(240)
      .refine((value) => !/[<>]/.test(value) && !/javascript:/i.test(value), {
        message: "Scene element content must be plain text.",
      }),
    label: z
      .string()
      .trim()
      .min(1)
      .max(80)
      .refine((value) => !/[<>]/.test(value), {
        message: "Scene element label must be plain text.",
      })
      .optional(),
    tone: z.enum(["neutral", "attention", "success", "incorrect"]).default("neutral"),
  })
  .strict();

const WhiteboardCoordinateSchema = z.number().min(0).max(100);
const WhiteboardDrawingPointSchema = z.object({
  x: WhiteboardCoordinateSchema,
  y: WhiteboardCoordinateSchema,
});
const WhiteboardDrawingLineOpSchema = z.object({
  op: z.literal("draw"),
  shape: z.enum(["arrow", "line"]),
  /** Normalized board coordinates, 0-100. */
  x: WhiteboardCoordinateSchema,
  y: WhiteboardCoordinateSchema,
  x2: WhiteboardCoordinateSchema,
  y2: WhiteboardCoordinateSchema,
  label: z.string().optional(),
});
const WhiteboardDrawingAreaOpSchema = z.object({
  op: z.literal("draw"),
  shape: z.enum(["circle", "box", "bracket"]),
  /** Normalized top-left board coordinates, 0-100. */
  x: WhiteboardCoordinateSchema,
  y: WhiteboardCoordinateSchema,
  width: z.number().min(1).max(100),
  height: z.number().min(1).max(100),
  side: z.enum(["left", "right", "top", "bottom"]).optional(),
  label: z.string().optional(),
});
const WhiteboardDrawingPathOpSchema = z.object({
  op: z.literal("draw"),
  shape: z.literal("path"),
  /** Freehand-style normalized points, 0-100. */
  points: z.array(WhiteboardDrawingPointSchema).min(2).max(12),
  label: z.string().optional(),
});

export const WhiteboardUpdateOpSchema = z.union([
  /** Pulse/circle matching text on the current board for a few seconds. */
  z.object({ op: z.literal("highlight"), target: z.string(), targetId: z.string().optional() }),
  /** Persistent tutor mark attached to matching board text. */
  z.object({
    op: z.literal("mark"),
    target: z.string(),
    targetId: z.string().optional(),
    style: WhiteboardMarkStyleSchema.optional(),
    label: z.string().optional(),
  }),
  /** Remove persistent tutor marks, leaving the exercise visible. */
  z.object({ op: z.literal("clear_marks") }),
  /** Show a semantic relationship between two visible text targets. */
  z.object({
    op: z.literal("connect"),
    from: z.string(),
    to: z.string(),
    fromId: z.string().optional(),
    toId: z.string().optional(),
    label: z.string().optional(),
  }),
  /** Remove semantic relationship callouts, leaving marks/drawings visible. */
  z.object({ op: z.literal("clear_connections") }),
  /** Draw a free-position shape on the board using normalized coordinates. */
  z.union([
    WhiteboardDrawingLineOpSchema,
    WhiteboardDrawingAreaOpSchema,
    WhiteboardDrawingPathOpSchema,
  ]),
  /** Remove free-position drawings, leaving the exercise visible. */
  z.object({ op: z.literal("clear_drawings") }),
  /** Tutor margin note shown under the exercise (replaces the previous one). */
  z.object({ op: z.literal("annotate"), note: z.string() }),
  /** Add or replace one bounded, tutor-authored semantic board element. */
  z.object({ op: z.literal("add_element"), element: WhiteboardSceneElementSchema }).strict(),
  /** Remove one tutor-authored semantic board element by stable id. */
  z
    .object({
      op: z.literal("remove_element"),
      elementId: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/),
    })
    .strict(),
  /** Remove all tutor-authored semantic elements while retaining the exercise. */
  z.object({ op: z.literal("clear_elements") }).strict(),
  /**
   * Trusted maths step compiled by the server from a semantic algebra operation.
   * The model never supplies these equation fields directly.
   */
  z
    .object({
      op: z.literal("set_equation_step"),
      stepId: z.string().min(1).max(160),
      operationId: z.string().min(1).max(160),
      operationLabel: z.string().min(1).max(160),
      workingEquation: z.string().min(3).max(240).optional(),
      visualDetail: z.enum(["result_only", "show_both_sides"]),
      equation: z.string().min(3).max(160),
      left: z.string().min(1).max(80),
      right: z.string().min(1).max(80),
      choices: z
        .array(
          z
            .object({
              id: z.string().min(1),
              label: z.string().min(1),
              operationId: z.string().min(1),
              correct: z.boolean(),
            })
            .strict()
        )
        .min(2)
        .max(4),
      status: z.enum(["guided", "verifying"]),
    })
    .strict(),
  /** Set or replace the hint line (gap_fill / word_tiles). */
  z.object({ op: z.literal("set_hint"), hint: z.string() }),
  /** Add an option live (gap_fill / quick_choice). */
  z.object({ op: z.literal("add_option"), option: z.string() }),
  /** Remove a distractor live (gap_fill / quick_choice). */
  z.object({ op: z.literal("remove_option"), option: z.string() }),
  /** Lock the first N tiles of the answer in place (word_tiles). */
  z.object({ op: z.literal("lock_tiles"), count: z.number().int().min(1) }),
]);

export type WhiteboardUpdateOp = z.infer<typeof WhiteboardUpdateOpSchema>;
export type WhiteboardMarkStyle = z.infer<typeof WhiteboardMarkStyleSchema>;
export type WhiteboardDrawingShape = z.infer<typeof WhiteboardDrawingShapeSchema>;
export type WhiteboardSceneElement = z.infer<typeof WhiteboardSceneElementSchema>;

// ─── Agent → frontend messages ───────────────────────────────────────

export const WhiteboardAgentMessageSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("whiteboard_show"), spec: WhiteboardSpecSchema }),
  z.object({ type: z.literal("whiteboard_clear"), exerciseId: z.string().optional() }),
  z.object({
    type: z.literal("whiteboard_update"),
    exerciseId: z.string().optional(),
    ops: z.array(WhiteboardUpdateOpSchema).min(1).max(6),
  }),
]);

export type WhiteboardAgentMessage = z.infer<typeof WhiteboardAgentMessageSchema>;

// ─── Frontend → agent interaction events ─────────────────────────────

export type WhiteboardEventName =
  | "answered"
  | "completed"
  | "working"
  | "reading_complete"
  | "speech_attempt"
  | "presented"
  | "hidden"
  | "closed"
  | "auto_hidden";

export interface WhiteboardInteractionEvent {
  type: "whiteboard_event";
  exerciseId: string;
  kind: WhiteboardKind;
  event: WhiteboardEventName;
  detail?: {
    /** What the learner tapped / built. */
    response?: string;
    /** Omitted for practice evidence and neutral speech-transcript observations. */
    correct?: boolean;
    /** Compact action label, e.g. selected_option, pair_match, opened_reveal. */
    action?: string;
    /** Human-readable board target, such as the sentence, prompt, or block label. */
    target?: string;
    /** Expected answer/board state when useful for tutor feedback. */
    expected?: string;
    /** Board-level progress summary, especially for multi-block composed boards. */
    progress?: string;
    /** True when this interaction resolved the current board task. */
    resolved?: boolean;
    /** 1-based attempt counter within this exercise. */
    attempt?: number;
    /** For composed exercises: which block the event came from. */
    block?: string;
    /** Stable target id for precise tutor references when a block provides one. */
    targetId?: string;
  };
}

/** Normalize for forgiving answer comparison (gap fill typing, word tiles). */
export function normalizeAnswer(value: string): string {
  return value
    .toLowerCase()
    .replace(/[.,!?;:¡¿"'«»]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}
